<script>
import api from "@/helpers/api";
import { swalError, swalSuccess, swalConfirm } from "@/helpers/swalHelper";
import { canView } from "@/helpers/permissions";

const STORAGE_PREFIX = "gridPrefs:";
// Above this row count, Print confirms with the user before rendering — the
// HTML popup can stutter on multi-MB payloads.
const PRINT_ROW_WARN_THRESHOLD = 2000;

/**
 * Reusable Export (Excel/CSV/PDF) + Print + Column-Visibility toolbar for ERP grids.
 *
 * Pattern: parent owns filter state and exposes it via a function returned
 * from `:filter-params`. The component calls `${endpoint}/export?format=...`
 * for downloads, and `${endpoint}` (with `length: 10000`) for print.
 *
 * Two column families (both optional, both opt-in via `pageKey`):
 *   1. `printColumns`     — columns shown in the on-screen table. Default visible.
 *                           User toggles to hide; CSS injected via `[data-col-key]`.
 *   2. `extraExportColumns` — entity fields NOT in the table but available for
 *                           Export/Print (e.g. Part Number, MRP, HSN/SAC).
 *                           Default hidden. User opts in via the dropdown.
 *
 * Persisted format (localStorage["gridPrefs:" + pageKey]):
 *   `{ hiddenInTable: ["k1", ...], shownInExport: ["k2", ...] }`
 *   Stores deltas from default — so newly-added columns get the correct default
 *   (visible / hidden) without invalidating saved prefs. Legacy Phase-1 string
 *   array (hidden printColumns keys only) is auto-migrated on load.
 *
 * Globally registered in main.js so list views can drop it in without imports.
 */
export default {
  name: "ExportPrintMenu",
  props: {
    endpoint: { type: String, required: true },
    filterParams: { type: Function, required: true },
    title: { type: String, required: true },
    fileNameBase: { type: String, default: null },
    printColumns: { type: Array, required: true },
    permissionKey: { type: String, default: null },
    extraExportParams: { type: Object, default: () => ({}) },
    /**
     * Stable identifier for this grid (e.g. "stock-items"). When set, enables
     * the Columns dropdown and persists user choices to
     * localStorage["gridPrefs:" + pageKey]. Each printColumns and
     * extraExportColumns entry must have a non-empty `key`.
     */
    pageKey: { type: String, default: null },
    /**
     * Two-way bound: union of column keys currently NOT visible — i.e. table
     * columns the user hid PLUS extra export columns the user has not opted in
     * to. List views just bind a `data()` field to it; the toolbar manages the
     * defaults and persistence.
     */
    hiddenKeys: { type: Array, default: () => [] },
    /**
     * Optional: extra entity fields available for Export/Print but not in the
     * on-screen table. Same shape as `printColumns` (header / key / value /
     * align / type / format). Default hidden — user opts in via the dropdown.
     */
    extraExportColumns: { type: Array, default: () => [] },
  },
  emits: ["update:hiddenKeys"],
  data() {
    return {
      exporting: false,
      printing: false,
    };
  },
  computed: {
    canShow() {
      if (!this.permissionKey) return true;
      return canView(this.permissionKey);
    },
    tableKeys() {
      return this.printColumns.map((c) => c.key);
    },
    extraKeys() {
      return this.extraExportColumns.map((c) => c.key);
    },
    allColumns() {
      return [...this.printColumns, ...this.extraExportColumns];
    },
    columnVisibilityEnabled() {
      if (!this.pageKey) return false;
      if (this.allColumns.length === 0) return false;
      return this.allColumns.every((c) => typeof c.key === "string" && c.key.length > 0);
    },
    /** Columns currently selected for Print + Excel/CSV/PDF (table cells use CSS). */
    visibleColumns() {
      if (!this.columnVisibilityEnabled) return this.printColumns;
      const hidden = new Set(this.hiddenKeys);
      return this.allColumns.filter((c) => !hidden.has(c.key));
    },
    /** Hidden printColumns keys only — drives the table-cell CSS hide. */
    tableHiddenKeys() {
      const hidden = new Set(this.hiddenKeys);
      return this.tableKeys.filter((k) => hidden.has(k));
    },
    /** Count of deviations from default state (badge on the Columns button). */
    deviationCount() {
      const hiddenSet = new Set(this.hiddenKeys);
      const tableHidden = this.tableKeys.filter((k) => hiddenSet.has(k)).length;
      const extrasShown = this.extraKeys.filter((k) => !hiddenSet.has(k)).length;
      return tableHidden + extrasShown;
    },
    storageKey() {
      return this.pageKey ? STORAGE_PREFIX + this.pageKey : null;
    },
    styleElementId() {
      return this.pageKey ? "grid-prefs-style-" + this.pageKey : null;
    },
  },
  watch: {
    hiddenKeys: {
      handler() { this.persistAndInject(); },
      deep: true,
    },
    pageKey() {
      this.removeInjectedStyle();
      this.loadFromStorage();
    },
    printColumns: {
      handler() { this.persistAndInject(); },
      deep: true,
    },
    extraExportColumns: {
      handler() { this.persistAndInject(); },
      deep: true,
    },
  },
  mounted() {
    if (this.columnVisibilityEnabled) this.loadFromStorage();
    this.persistAndInject();
  },
  beforeUnmount() {
    this.removeInjectedStyle();
  },
  methods: {
    /**
     * Reconstruct hiddenKeys from persisted deltas. Backward-compatible with
     * Phase 1's flat-array format (treated as `hiddenInTable` only). Unknown
     * keys are dropped. Missing extras default to hidden; missing table
     * columns default to visible.
     */
    loadFromStorage() {
      if (!this.storageKey) return;
      const tableSet = new Set(this.tableKeys);
      const extraSet = new Set(this.extraKeys);

      let hiddenInTable = [];
      let shownInExport = [];
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            // Legacy Phase-1 format — flat array of hidden printColumns keys.
            hiddenInTable = parsed.filter((k) => typeof k === "string" && tableSet.has(k));
          } else if (parsed && typeof parsed === "object") {
            hiddenInTable = (Array.isArray(parsed.hiddenInTable) ? parsed.hiddenInTable : [])
              .filter((k) => typeof k === "string" && tableSet.has(k));
            shownInExport = (Array.isArray(parsed.shownInExport) ? parsed.shownInExport : [])
              .filter((k) => typeof k === "string" && extraSet.has(k));
          }
        }
      } catch { /* ignore corrupt JSON */ }

      const hidden = [
        ...this.tableKeys.filter((k) => hiddenInTable.includes(k)),
        ...this.extraKeys.filter((k) => !shownInExport.includes(k)),
      ];
      this.emitIfDifferent(hidden);
    },

    emitIfDifferent(newHiddenKeys) {
      const a = newHiddenKeys;
      const b = this.hiddenKeys;
      if (a.length !== b.length || a.some((k, i) => b[i] !== k)) {
        this.$emit("update:hiddenKeys", newHiddenKeys);
      }
    },

    persistAndInject() {
      if (!this.columnVisibilityEnabled) {
        this.removeInjectedStyle();
        return;
      }
      try {
        const hiddenSet = new Set(this.hiddenKeys);
        const hiddenInTable = this.tableKeys.filter((k) => hiddenSet.has(k));
        const shownInExport = this.extraKeys.filter((k) => !hiddenSet.has(k));
        if (hiddenInTable.length === 0 && shownInExport.length === 0) {
          localStorage.removeItem(this.storageKey);
        } else {
          localStorage.setItem(
            this.storageKey,
            JSON.stringify({ hiddenInTable, shownInExport })
          );
        }
      } catch { /* localStorage may be full / disabled */ }
      this.injectStyle();
    },

    /** Hide table cells whose data-col-key matches a hidden printColumns key. */
    injectStyle() {
      if (!this.styleElementId) return;
      let el = document.getElementById(this.styleElementId);
      const tableHidden = this.tableHiddenKeys;
      if (tableHidden.length === 0) {
        if (el) el.textContent = "";
        return;
      }
      if (!el) {
        el = document.createElement("style");
        el.id = this.styleElementId;
        document.head.appendChild(el);
      }
      const selectors = tableHidden
        .map((k) => `[data-col-key="${this.cssEscape(k)}"]`)
        .join(",");
      el.textContent = `${selectors} { display: none !important; }`;
    },

    removeInjectedStyle() {
      if (!this.styleElementId) return;
      const el = document.getElementById(this.styleElementId);
      if (el) el.remove();
    },

    cssEscape(s) {
      return String(s).replace(/(["\\])/g, "\\$1");
    },

    toggleColumn(key) {
      const hidden = new Set(this.hiddenKeys);
      if (hidden.has(key)) {
        hidden.delete(key);
      } else {
        // Guard: don't allow hiding the last visible TABLE column. Hiding all
        // extras is fine — that's their default state.
        if (this.tableKeys.includes(key)) {
          const visibleTable = this.tableKeys.filter((k) => !hidden.has(k));
          if (visibleTable.length <= 1) return;
        }
        hidden.add(key);
      }
      // Preserve printColumns + extras declaration order in the emitted array.
      const allKeys = [...this.tableKeys, ...this.extraKeys];
      const ordered = allKeys.filter((k) => hidden.has(k));
      this.$emit("update:hiddenKeys", ordered);
    },

    isColumnVisible(key) {
      return !this.hiddenKeys.includes(key);
    },

    isToggleDisabled(key) {
      // Only printColumns can hit the last-column guard. Extras are unrestricted.
      if (!this.tableKeys.includes(key)) return false;
      if (!this.isColumnVisible(key)) return false;
      const visibleTable = this.tableKeys.filter((k) => !this.hiddenKeys.includes(k));
      return visibleTable.length <= 1;
    },

    resetColumns() {
      // Defaults: every printColumns key visible, every extra key hidden.
      const defaults = [...this.extraKeys];
      this.emitIfDifferent(defaults);
    },

    async exportAs(format) {
      if (this.exporting || this.printing) return;
      this.exporting = true;
      try {
        const params = { format, ...this.filterParams(), ...this.extraExportParams };
        if (this.columnVisibilityEnabled) {
          params.columns = this.visibleColumns.map((c) => c.key).join(",");
        }
        const response = await api.get(`${this.endpoint}/export`, {
          params,
          responseType: "blob",
        });
        this.triggerDownload(response, format);
        swalSuccess("Export completed");
      } catch (err) {
        const msg = await this.readBlobMessage(err)
          || (err.response?.status === 403 ? "You do not have permission to export." : "Export failed.");
        swalError(msg);
      } finally {
        this.exporting = false;
      }
    },

    async readBlobMessage(err) {
      const data = err.response?.data;
      if (!data || typeof data.text !== "function") return null;
      try {
        const txt = await data.text();
        const json = JSON.parse(txt);
        return json.message || null;
      } catch { return null; }
    },

    triggerDownload(response, format) {
      const cd = response.headers["content-disposition"] || "";
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const ext = format === "excel" ? "xlsx" : format;
      const fallback = `${this.fileNameBase || "export"}-${new Date().toISOString().slice(0,10)}.${ext}`;
      const fileName = match ? decodeURIComponent(match[1]) : fallback;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      try {
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Always revoke even when click() throws (rare but possible in
        // restrictive contexts — without finally, the blob URL leaks).
        window.URL.revokeObjectURL(url);
      }
    },

    async printGrid() {
      if (this.exporting || this.printing) return;
      this.printing = true;
      try {
        const colsCsv = this.columnVisibilityEnabled
          ? this.visibleColumns.map((c) => c.key).join(",")
          : null;
        let rows;
        if (this.columnVisibilityEnabled && this.extraExportColumns.length > 0) {
          // Extras are not in the list-endpoint shape — fetch via the export
          // endpoint as JSON so Print sees the same rich rows as Excel/PDF.
          const params = { ...this.filterParams(), format: "json", columns: colsCsv };
          const { data } = await api.get(`${this.endpoint}/export`, { params });
          rows = Array.isArray(data) ? data : [];
        } else {
          const params = { ...this.filterParams(), draw: 1, start: 0, length: 10000 };
          if (colsCsv) params.columns = colsCsv;
          const { data } = await api.get(this.endpoint, { params });
          rows = Array.isArray(data) ? data : (data.data || data.results || []);
        }

        // Confirm with the user before rendering very large result sets — payload
        // can exceed 10 MB and the browser may stutter or freeze.
        if (rows.length >= PRINT_ROW_WARN_THRESHOLD) {
          const proceed = await swalConfirm(
            "Large print job",
            `This will print ${rows.length.toLocaleString("en-IN")} rows. ` +
            "The browser may take several seconds to render. Continue, or use Excel/PDF export instead?",
            { confirmText: "Print anyway", cancelText: "Cancel" }
          );
          if (!proceed) return;
        }
        this.openPrintWindow(rows);
      } catch (err) {
        swalError(err.response?.data?.message || "Could not load records for printing.");
      } finally {
        this.printing = false;
      }
    },

    openPrintWindow(rows) {
      const companies = JSON.parse(localStorage.getItem("companies") || "[]");
      const cid = parseInt(localStorage.getItem("companyId") || "0", 10);
      const co = companies.find((c) => c.id === cid);
      const companyName = (co && (co.shortName || co.name)) || "Integral ERP";
      const printDate = new Date().toLocaleString();
      const html = this.buildPrintHtml(companyName, printDate, rows);
      const w = window.open("", "_blank", "width=1100,height=800");
      if (!w) {
        swalError("Pop-up blocked. Please allow pop-ups for this site to print.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.onload = () => { w.focus(); w.print(); };
    },

    escapeHtml(s) {
      if (s === null || s === undefined) return "";
      return String(s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
    },

    formatCellValue(value, col) {
      if (typeof col.format === "function") return col.format(value);
      if (col.type === "yesno") return value ? "Yes" : "No";
      if (col.type === "number") {
        if (value === null || value === undefined || value === 0) return "—";
        return Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (col.type === "integer") {
        if (value === null || value === undefined) return "—";
        return Number(value).toLocaleString("en-IN");
      }
      if (value === null || value === undefined || value === "") return "—";
      return String(value);
    },

    cellHtml(row, col) {
      const raw = typeof col.value === "function"
        ? col.value(row)
        : (col.key ? row[col.key] : "");
      const formatted = this.formatCellValue(raw, col);
      const escaped = col.html === true ? formatted : this.escapeHtml(formatted);
      const align = col.align ? ` style="text-align:${col.align}"` : "";
      return `<td${align}>${escaped}</td>`;
    },

    buildPrintHtml(companyName, printDate, rows) {
      const cols = this.visibleColumns;
      const thead = cols.map((c) => {
        const align = c.align ? ` style="text-align:${c.align}"` : "";
        return `<th${align}>${this.escapeHtml(c.header)}</th>`;
      }).join("");

      const tbody = rows.length === 0
        ? `<tr><td colspan="${cols.length}" style="text-align:center;color:#888;padding:24px">No records found.</td></tr>`
        : rows.map((r) => `<tr>${cols.map((c) => this.cellHtml(r, c)).join("")}</tr>`).join("");

      return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${this.escapeHtml(this.title)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: Arial, Helvetica, sans-serif; color: #212529; margin: 0; padding: 16px; }
  .hdr { border-bottom: 2px solid #0d6efd; padding-bottom: 8px; margin-bottom: 12px; }
  .company { font-size: 18px; font-weight: 700; color: #0d6efd; }
  .title { font-size: 14px; font-weight: 700; margin-top: 2px; }
  .meta { font-size: 11px; color: #6c757d; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #dee2e6; padding: 5px 6px; vertical-align: top; }
  thead th { background: #0d6efd; color: #fff; text-align: left; }
  tbody tr:nth-child(even) td { background: #f8f9fa; }
  .footer { margin-top: 10px; font-size: 10px; color: #6c757d; text-align: right; }
  @media print { .no-print { display: none; } }
</style></head>
<body>
  <div class="hdr">
    <div class="company">${this.escapeHtml(companyName)}</div>
    <div class="title">${this.escapeHtml(this.title)}</div>
    <div class="meta">Printed: ${this.escapeHtml(printDate)}  •  ${rows.length} record(s)</div>
  </div>
  <table>
    <thead><tr>${thead}</tr></thead>
    <tbody>${tbody}</tbody>
  </table>
  <div class="footer">Generated by Integral ERP</div>
</body></html>`;
    },
  },
};
</script>

<template>
  <div v-if="canShow" class="d-inline-flex gap-2 flex-wrap">
    <div class="btn-group btn-group-sm">
      <button
        type="button"
        class="btn btn-outline-primary dropdown-toggle"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        :disabled="exporting || printing"
        title="Export filtered records"
      >
        <span v-if="exporting" class="spinner-border spinner-border-sm me-1" role="status"></span>
        <i v-else class="ri-download-2-line"></i>
        Export
      </button>
      <ul class="dropdown-menu dropdown-menu-end">
        <li>
          <a class="dropdown-item" href="javascript:void(0)" @click="exportAs('excel')">
            <i class="ri-file-excel-2-line text-success me-1"></i> Excel (.xlsx)
          </a>
        </li>
        <li>
          <a class="dropdown-item" href="javascript:void(0)" @click="exportAs('csv')">
            <i class="ri-file-text-line text-secondary me-1"></i> CSV
          </a>
        </li>
        <li>
          <a class="dropdown-item" href="javascript:void(0)" @click="exportAs('pdf')">
            <i class="ri-file-pdf-2-line text-danger me-1"></i> PDF
          </a>
        </li>
      </ul>
    </div>
    <button
      class="btn btn-outline-secondary btn-sm"
      :disabled="exporting || printing"
      @click="printGrid"
      title="Print filtered records"
    >
      <span v-if="printing" class="spinner-border spinner-border-sm me-1" role="status"></span>
      <i v-else class="ri-printer-line"></i>
      Print
    </button>
    <div v-if="columnVisibilityEnabled" class="btn-group btn-group-sm">
      <button
        type="button"
        class="btn btn-outline-secondary dropdown-toggle"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-expanded="false"
        :disabled="exporting || printing"
        title="Show or hide columns"
      >
        <i class="ri-layout-column-line"></i>
        Columns
        <span v-if="deviationCount" class="badge bg-secondary ms-1">{{ deviationCount }}</span>
      </button>
      <ul
        class="dropdown-menu dropdown-menu-end p-2"
        style="min-width: 240px; max-height: 70vh; overflow-y: auto;"
      >
        <template v-if="printColumns.length">
          <li>
            <h6 class="dropdown-header py-1 px-2 text-muted small text-uppercase">In table</h6>
          </li>
          <li v-for="col in printColumns" :key="col.key">
            <label class="dropdown-item d-flex align-items-center gap-2 mb-0" style="cursor: pointer;">
              <input
                type="checkbox"
                class="form-check-input m-0"
                :checked="isColumnVisible(col.key)"
                :disabled="isToggleDisabled(col.key)"
                @change="toggleColumn(col.key)"
              />
              <span class="flex-grow-1">{{ col.header }}</span>
            </label>
          </li>
        </template>
        <template v-if="extraExportColumns.length">
          <li v-if="printColumns.length"><hr class="dropdown-divider" /></li>
          <li>
            <h6 class="dropdown-header py-1 px-2 text-muted small text-uppercase">
              Extra export fields
            </h6>
          </li>
          <li v-for="col in extraExportColumns" :key="col.key">
            <label class="dropdown-item d-flex align-items-center gap-2 mb-0" style="cursor: pointer;">
              <input
                type="checkbox"
                class="form-check-input m-0"
                :checked="isColumnVisible(col.key)"
                @change="toggleColumn(col.key)"
              />
              <span class="flex-grow-1">{{ col.header }}</span>
            </label>
          </li>
        </template>
        <li><hr class="dropdown-divider" /></li>
        <li>
          <button
            type="button"
            class="dropdown-item text-primary"
            :disabled="deviationCount === 0"
            @click="resetColumns"
          >
            <i class="ri-refresh-line me-1"></i> Reset to default
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
